import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { StatusBadge } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Plus, Trash2, CalendarClock, Users, Send } from "lucide-react";
import { toast } from "sonner";

export default function Assignments() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ program_id: "", title: "", description: "", due_date: "", max_grade: 100 });
  const canManage = user.role === "admin" || user.role === "supervisor" || user.role === "teacher";

  const load = () => api.get("/assignments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); if (canManage) api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {}); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    try { await api.post("/assignments", { ...form, max_grade: Number(form.max_grade) }); toast.success("تمت إضافة الواجب"); setOpen(false); setForm({ program_id: "", title: "", description: "", due_date: "", max_grade: 100 }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("حذف الواجب؟")) return; try { await api.delete(`/assignments/${id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (items === null) return <PageLoader />;

  const asgTones = [
    "border-emerald-100 bg-emerald-50/50",
    "border-amber-100 bg-amber-50/50",
    "border-sky-100 bg-sky-50/50",
    "border-violet-100 bg-violet-50/50",
    "border-rose-100 bg-rose-50/50",
    "border-teal-100 bg-teal-50/50",
  ];

  return (
    <>
      <PageHeader title="الواجبات" subtitle="المهام العلمية وتقييمها"
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-assignment-btn" className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-2 h-4 w-4" /> إضافة واجب</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-navy">إضافة واجب</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div><Label>البرنامج</Label>
                  <Select value={form.program_id} onValueChange={set("program_id")}><SelectTrigger data-testid="asg-program" className="mt-1.5 rounded-lg"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>عنوان الواجب</Label><Input data-testid="asg-title" value={form.title} onChange={(e) => set("title")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>وصف المطلوب</Label><Textarea value={form.description} onChange={(e) => set("description")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>موعد التسليم</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                  <div><Label>الدرجة</Label><Input type="number" value={form.max_grade} onChange={(e) => set("max_grade")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="asg-save" onClick={submit} disabled={!form.program_id || !form.title} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      {items.length === 0 ? <EmptyState icon={ClipboardList} title="لا توجد واجبات" /> : (
        <div className="stagger grid gap-4">
          {items.map((a, i) => (
            <div key={a.id} data-testid={`assignment-${a.id}`} className={`rounded-2xl border p-5 soft-shadow ${asgTones[i % asgTones.length]}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{a.program_name}</span>
                  <h3 className="mt-2 font-heading text-lg font-bold text-navy">{a.title}</h3>
                  <p className="mt-1 text-sm text-slate-500">{a.description || "—"}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> التسليم: {a.due_date || "—"}</span>
                    <span>الدرجة: {a.max_grade}</span>
                    {canManage && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {a.submissions_count} تسليم</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canManage && <SubmissionsDialog assignment={a} />}
                  {user.role === "student" && <StudentSubmit assignment={a} onDone={load} />}
                  {canManage && <Button onClick={() => remove(a.id)} data-testid={`del-asg-${a.id}`} size="sm" variant="ghost" className="text-red-400 hover:bg-red-50 hover:text-red-500 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function StudentSubmit({ assignment, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(assignment.my_submission?.text || "");
  const sub = assignment.my_submission;

  const submit = async () => {
    try { await api.post("/submissions", { assignment_id: assignment.id, text }); toast.success("تم تسليم الواجب"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      {sub?.status === "graded" && <span className="me-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">الدرجة: {sub.grade}</span>}
      {sub ? <StatusBadge status={sub.status} /> : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button data-testid={`submit-asg-${assignment.id}`} size="sm" className="rounded-lg bg-greenx hover:bg-greenx-light"><Send className="me-1 h-4 w-4" /> {sub ? "تعديل" : "تسليم"}</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-navy">تسليم الواجب: {assignment.title}</DialogTitle></DialogHeader>
          <Textarea data-testid="submission-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="اكتب إجابتك أو رابط الملف هنا..." rows={6} className="rounded-lg" />
          {sub?.feedback && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">ملاحظة المدرس: {sub.feedback}</p>}
          <DialogFooter><Button data-testid="submission-save" onClick={submit} disabled={!text} className="rounded-xl bg-navy hover:bg-navy-light">إرسال</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SubmissionsDialog({ assignment }) {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const load = () => api.get(`/assignments/${assignment.id}/submissions`).then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const grade = async (id, g, feedback) => {
    try { await api.put(`/submissions/${id}/grade`, { grade: Number(g), feedback }); toast.success("تم التقييم"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`view-subs-${assignment.id}`} size="sm" variant="outline" className="rounded-lg">التسليمات</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-navy">تسليمات: {assignment.title}</DialogTitle></DialogHeader>
        {subs.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">لا توجد تسليمات</p> : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-100 p-4">
                <div className="flex items-center justify-between"><p className="font-semibold text-navy">{s.student_name}</p><StatusBadge status={s.status} /></div>
                <p className="mt-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{s.text || "—"}</p>
                <GradeInput sub={s} max={assignment.max_grade} onGrade={grade} />
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GradeInput({ sub, max, onGrade }) {
  const [g, setG] = useState(sub.grade ?? "");
  const [fb, setFb] = useState(sub.feedback ?? "");
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2">
      <div><Label className="text-xs">الدرجة (من {max})</Label><Input type="number" value={g} onChange={(e) => setG(e.target.value)} className="mt-1 h-9 w-24 rounded-lg" /></div>
      <div className="flex-1"><Label className="text-xs">ملاحظة</Label><Input value={fb} onChange={(e) => setFb(e.target.value)} className="mt-1 h-9 rounded-lg" /></div>
      <Button onClick={() => onGrade(sub.id, g, fb)} size="sm" className="rounded-lg bg-navy hover:bg-navy-light">تقييم</Button>
    </div>
  );
}
